import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  REQUEST,
} from '@angular/core';
import { CookieStorageEngine } from '@app/cookie-storage-engine';
import { OrderDetails } from '@app/entities/orders/interfaces/order-details';
import { OrderItem } from '@app/entities/orders/interfaces/order-item';
import { PaymentDetails } from '@app/entities/orders/interfaces/payment-details';
import { OrdersService } from '@app/entities/orders/services/orders.service';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs';
import { CartStateModel } from './cart-state.model';
import {
  AddToCart,
  ClearCart,
  CreateOrder,
  LoadOrderDetails,
  RemoveFromCart,
  UpadteOrderAmout,
} from './cart.actions';

@State<CartStateModel>({
  name: 'cart',
  defaults: {
    imageDetailsIds: [],
  },
})
@Injectable()
export class CartState {
  constructor(
    private ordersService: OrdersService,
    private cookieStorage: CookieStorageEngine,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: Request, // Inject the request object
  ) {}

  ngxsOnInit({ setState }: StateContext<CartStateModel>): void {
    if (!isPlatformServer(this.platformId) || !this.request) {
      return;
    }

    const cookieName = 'cart';
    const cookie = this.cookieStorage.getItem(cookieName);
    if (cookie) {
      try {
        setState(JSON.parse(cookie));
      } catch (error) {
        console.error(`Error parsing ${cookieName} cookie in SSR:`, error);
      }
    }
  }

  @Action(CreateOrder)
  @ImmutableContext()
  createOrder(
    { getState }: StateContext<CartStateModel>,
    { data }: CreateOrder,
  ) {
    const imageDetailIds = getState().imageDetailsIds;
    if (imageDetailIds.length === 0) {
      return;
    }

    return this.ordersService.createOrder(imageDetailIds, data).pipe(
      tap((paymentDetails: PaymentDetails) => {
        window.location.href = paymentDetails.redirect_url;
      }),
    );
  }

  @Action(LoadOrderDetails)
  @ImmutableContext()
  loadOrderDetails({ getState, setState }: StateContext<CartStateModel>) {
    const imageDetailIds = getState().imageDetailsIds;

    return this.ordersService.loadOrderDetails(imageDetailIds).pipe(
      tap((orderDetails: OrderDetails) => {
        setState((state: CartStateModel) => {
          state.orderDetails = orderDetails;
          return state;
        });
      }),
    );
  }

  @Action(UpadteOrderAmout)
  @ImmutableContext()
  upadteOrderAmout(
    { setState }: StateContext<CartStateModel>,
    { taxRate }: UpadteOrderAmout,
  ) {
    return setState((state: CartStateModel) => {
      if (!state.orderDetails) {
        return state;
      }

      let totalAmount = 0;
      state.orderDetails.items.forEach((item: OrderItem) => {
        item.amount = (item.price * (100 + taxRate)) / 100;
        totalAmount += item.amount;
      });

      state.orderDetails.total_amount = totalAmount;

      return state;
    });
  }

  @Action(AddToCart)
  @ImmutableContext()
  addToCart(
    { setState }: StateContext<CartStateModel>,
    { imageDetailsId }: AddToCart,
  ) {
    return setState((state: CartStateModel) => {
      if (!state.imageDetailsIds.includes(imageDetailsId)) {
        state.imageDetailsIds.push(imageDetailsId);
      }
      return state;
    });
  }

  @Action(ClearCart)
  @ImmutableContext()
  clearCart({ setState }: StateContext<CartStateModel>) {
    return setState((state: CartStateModel) => {
      state.imageDetailsIds = [];
      state.orderDetails = undefined;
      return state;
    });
  }

  @Action(RemoveFromCart)
  @ImmutableContext()
  removeFromCart(
    { setState }: StateContext<CartStateModel>,
    { imageDetailsId }: AddToCart,
  ) {
    return setState((state: CartStateModel) => {
      const index = state.imageDetailsIds.indexOf(imageDetailsId);
      if (index !== -1) {
        state.imageDetailsIds.splice(index, 1); // Removes the element at the found index
        const item = state.orderDetails?.items.find((item: OrderItem) => {
          return item.id === imageDetailsId;
        });
        if (item) {
          state.orderDetails!.items = state.orderDetails!.items.filter(
            (item: OrderItem) => {
              return item.id !== imageDetailsId;
            },
          );

          state.orderDetails!.total_amount -= item.amount;
        }
      }

      return state;
    });
  }
}
